import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { registerSW } from "virtual:pwa-register";
import App from "./App";
import "./index.css";
// Load i18n eagerly so its module-level side effects (including the
// runtime auto-translator) fire BEFORE React mounts the first component
// tree. Without this, autoTranslate only gets set up whenever the first
// component that uses useTranslation renders — which is after the sidebar
// has already committed to the DOM.
import "./i18n";

// Register the service worker generated by vite-plugin-pwa. autoUpdate mode
// swaps the SW the moment a new build is FOUND — but a browser only looks for
// one on navigation, and HQ lives in tabs that stay open for days. That gap is
// how "it works for me, not for him" happens: whoever reloads runs today's
// build, whoever doesn't runs last week's, and a feature that shipped in
// between simply does not exist on their screen.
//
// So the tab checks for itself: the moment it becomes visible again (the
// person just came back — a swap right now interrupts nothing), and every half
// hour while it sits HIDDEN (a background tab can refresh harmlessly and be
// current by the time they return). Never on a timer while the tab is in use —
// autoUpdate reloads when the new worker takes control, and a surprise reload
// mid-order-entry costs somebody their form.
registerSW({
  immediate: true,
  onRegisteredSW(_url, registration) {
    if (!registration) return;
    const check = () => { void registration.update().catch(() => {}); };
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") check();
    });
    setInterval(() => {
      if (document.visibilityState === "hidden") check();
    }, 30 * 60 * 1000);
  },
});

// After a deploy, content-hashed chunk names change. A cached/older app shell
// can request a chunk that no longer exists (404), which used to leave a
// half-broken page that "only loads after a hard refresh". Vite fires
// `vite:preloadError` in that case — reload once to pull the fresh build.
// Guarded by a timestamp so it can never become a reload loop.
window.addEventListener("vite:preloadError", (e) => {
  e.preventDefault();
  const KEY = "scenta-hq-chunk-reload-at";
  const last = Number(sessionStorage.getItem(KEY) || "0");
  if (Date.now() - last < 15000) return; // already reloaded very recently — don't loop
  sessionStorage.setItem(KEY, String(Date.now()));
  window.location.reload();
});

// kiosk.scentafilter.com is the warehouse subdomain — force-route to /kiosk
// regardless of what path the user typed. The /kiosk page handles auth + UI.
const isKioskHost = /^kiosk\./i.test(window.location.hostname);
if (isKioskHost && window.location.pathname !== "/kiosk") {
  window.history.replaceState(null, "", "/kiosk");
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </React.StrictMode>,
);
